/**
 * TenantSettingsStore provides access to tenant-specific settings like maintenance state,
 * system messages, image assets and naming conventions. It combines React Query for data
 * fetching with Zustand for state management to provide a unified API for tenant settings.
 *
 * =============================================================================
 * CRITICAL: "CLEAN ROOM" THEME INITIALIZATION PATTERN
 * =============================================================================
 *
 * This store is a critical part of the tenant theming system. Gluestack-style v1
 * has a fundamental limitation: component theme tokens (like $brandBase in Button.ts)
 * are resolved at createComponents() time, NOT at render time.
 *
 * To work around this, we use a "clean room" initialization pattern:
 *
 * 1. gluestackConfig starts as NULL (not a default config!)
 * 2. ThemeWrapper in App.jsx waits (returns null) until gluestackConfig is set
 * 3. When tenant settings load, setSettings() calls generateDynamicTheme()
 * 4. generateDynamicTheme() calls createComponents() with the tenant's token values
 * 5. Only THEN does GluestackUIProvider mount with the correctly-configured themes
 *
 * DO NOT:
 * - Initialize gluestackConfig to baseConfig or any pre-built config
 * - Skip calling generateDynamicTheme() in setSettings()
 * - Mount GluestackUIProvider before tenant settings are loaded
 *
 * If you break this pattern, component themes will use default SNEXT colors
 * instead of tenant colors, even though inline props will work correctly.
 *
 * Related files:
 * - App.jsx (ThemeWrapper waits for gluestackConfig)
 * - themeGenerator.ts (creates fresh components with dynamic tokens)
 * - gluestack-ui.config.ts (exports componentsTheme for re-creation)
 * =============================================================================
 *
 * Evaluation Dimensions and Grading:
 * - Each tenant defines a set of evaluation dimensions (formal, description, etc.)
 * - Each dimension is evaluated using a performance level (very_good, good, etc.)
 * - Evaluations are stored as Record<dimensionKey, EvaluationDimension>
 * - EvaluationDimension contains a level (performance level ID) and optional comment
 * - Performance levels have weights (0-1) that determine their impact on grades
 */

import React from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import axios, { AxiosError } from 'axios';
import { useGetPublicTenantSettings } from '../api/endpoints/tenants/tenants';
import type { MaintenanceEventApi } from '../api/models/maintenanceEventApi';
import type { TenantPublicSettings } from '../api/models/tenantPublicSettings';
import type { TenantPublicGradingSettingsEvalDimensions } from '../api/models/tenantPublicGradingSettingsEvalDimensions';
import type { PerformanceLevelConfig } from '../api/models/performanceLevelConfig';
import { useAuthStore } from '../auth/store';
import type { EventKind } from '../api/models/eventKind';
import { TenantPublicTheme } from '../api/models/tenantPublicTheme';
import { generateDynamicTheme } from '../theme/themeGenerator';
import { MyNavigationTheme } from '../theme/navigationTheme';
import log from '../utils/logger';

const missingGradingFieldWarnings = new WeakMap<TenantPublicSettings, Set<string>>();

function warnMissingGradingFieldOnce(
  settings: TenantPublicSettings,
  getterKey: string,
  message: string,
): void {
  let warnedForSettings = missingGradingFieldWarnings.get(settings);
  if (!warnedForSettings) {
    warnedForSettings = new Set();
    missingGradingFieldWarnings.set(settings, warnedForSettings);
  }
  if (warnedForSettings.has(getterKey)) {
    return;
  }
  warnedForSettings.add(getterKey);
  log.warn(message);
}

interface TenantSettingsState {
  status: 'loading' | 'error' | 'success';
  error: Error | null;
  settings: TenantPublicSettings | null;
  setSettings: (settings: TenantPublicSettings | null) => void;
  getNextMaintenanceEvent: () => MaintenanceEventApi | null;
  isInMaintenance: () => boolean;
  setStatus: (status: 'loading' | 'error' | 'success') => void;
  setError: (error: Error) => void;
  // Grading settings methods
  getEvaluationDimensions: () => Record<string, string>;
  getRawEvaluationDimensions: () => TenantPublicGradingSettingsEvalDimensions;
  getEvaluationDimensionKeys: () => string[];
  getEditableDimensionKeys: () => string[];
  getOralExamDimensionKeys: () => string[];
  getPerformanceLevels: () => PerformanceLevelConfig[];
  getPerformanceLevelIds: () => string[];
  getDimensionColors: () => Record<string, string>;
  getDimensionLightColors: () => Record<string, string>;
  getLevelWeights: () => Record<string, number>;
  getRawDimensionMaxWeights: () => Record<string, number | null>;
  // Event kinds methods
  getEventKinds: () => EventKind[];
  getEventKindById: (id: string) => EventKind | null;
  // Theme methods
  getTheme: () => TenantPublicTheme | null;
  gluestackConfig: ReturnType<typeof generateDynamicTheme>['gluestackConfig'] | null;
  navigationTheme: typeof MyNavigationTheme;
  themeVersion: number;
  getLegacyCourseCompatibilityMode: () => boolean;
}

export const useTenantSettingsStore = create<TenantSettingsState>((set, get) => ({
  status: 'loading',
  error: null,
  settings: null,
  // IMPORTANT: Start with null to enable "clean room" initialization.
  // ThemeWrapper won't mount GluestackUIProvider until this is set,
  // ensuring component themes are created AFTER tenant tokens are known.
  gluestackConfig: null,
  navigationTheme: MyNavigationTheme,
  themeVersion: 0,

  setStatus: (status) => set({ status }),
  setError: (error) => set({ error }),
  setSettings: (settings) => {
    const current = get();
    const normalizedSettings = settings || null;

    // Identical poll payload — no store update, no remount.
    if (JSON.stringify(current.settings) === JSON.stringify(normalizedSettings)) {
      return;
    }

    const currentTheme = current.settings?.theme ?? null;
    const newTheme = normalizedSettings?.theme ?? null;
    const themeChanged = JSON.stringify(currentTheme) !== JSON.stringify(newTheme);

    // Non-theme field change with existing theme config — update settings only.
    if (!themeChanged && current.gluestackConfig !== null) {
      set({ settings: normalizedSettings });
      return;
    }

    // CRITICAL: Always generate a fresh config via generateDynamicTheme().
    // This calls createComponents() with the tenant's token values, ensuring
    // component themes (Button.ts, Input.ts, etc.) resolve tokens like $brandBase
    // to the tenant's colors. See "CLEAN ROOM" documentation at top of file.
    // DO NOT skip this or use a cached/pre-built config!
    const { gluestackConfig, navigationTheme } = generateDynamicTheme(newTheme);

    set({
      settings: normalizedSettings,
      gluestackConfig,
      navigationTheme,
      themeVersion: get().themeVersion + 1,
    });
  },

  getNextMaintenanceEvent: () => {
    const { settings } = get();
    if (!settings?.maintenanceState?.maintenanceEvents?.length) return null;

    const nowUtc = new Date().toISOString();
    const futureEvents = settings.maintenanceState.maintenanceEvents.filter((event) => event.startTime > nowUtc);
    return futureEvents.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())[0] || null;
  },

  isInMaintenance: () => {
    const { settings } = get();
    if (!settings?.maintenanceState?.maintenanceEvents?.length) return false;

    const nowUtc = new Date().toISOString();
    const isInMaintenance = settings.maintenanceState.maintenanceEvents
      .some((event) => event.startTime <= nowUtc && event.endTime >= nowUtc);

    if (isInMaintenance && process.env.EXPO_PUBLIC_IGNORE_MAINTENANCE === 'true') {
      log.info('Detected maintenance, but ignoring because of ENV setting');
      return false;
    }
    return isInMaintenance;
  },

  // Grading settings methods
  getEvaluationDimensions: () => {
    const { settings } = get();
    if (!settings) {
      return {};
    }
    if (!settings.gradingSettings?.evalDimensions) {
      warnMissingGradingFieldOnce(
        settings,
        'getEvaluationDimensions',
        'No evaluation dimensions found in tenant settings',
      );
      return {};
    }

    // For backward compatibility, transform the API response to a simple key-value dictionary
    // where keys are dimension labels and values are descriptions
    const dimensions = settings.gradingSettings.evalDimensions;
    const compatibleDimensions = {};

    Object.entries(dimensions).forEach(([key, dimension]) => {
      compatibleDimensions[key] = dimension.description;
    });

    return compatibleDimensions;
  },

  getRawEvaluationDimensions: () => {
    const { settings } = get();
    if (!settings) {
      return {};
    }
    if (!settings.gradingSettings?.evalDimensions) {
      warnMissingGradingFieldOnce(
        settings,
        'getRawEvaluationDimensions',
        'No evaluation dimensions found in tenant settings',
      );
      return {};
    }
    return settings.gradingSettings.evalDimensions;
  },

  getEvaluationDimensionKeys: () => {
    const dimensions = get().getEvaluationDimensions();
    return Object.keys(dimensions);
  },

  getEditableDimensionKeys: () => {
    const { settings } = get();
    if (!settings) {
      return [];
    }
    if (!settings.gradingSettings?.evalDimensions) {
      warnMissingGradingFieldOnce(
        settings,
        'getEditableDimensionKeys',
        'No evaluation dimensions found in tenant settings',
      );
      return [];
    }

    return Object.entries(settings.gradingSettings.evalDimensions)
      .filter(([, dimension]) => dimension.systemManaged !== true)
      .map(([key]) => key);
  },

  getOralExamDimensionKeys: () => {
    const { settings } = get();
    return settings?.gradingSettings?.oralExamDimensionKeys ?? [];
  },

  getPerformanceLevels: () => {
    const { settings } = get();
    if (!settings) {
      return [];
    }
    if (!settings.gradingSettings?.performanceLevels) {
      warnMissingGradingFieldOnce(
        settings,
        'getPerformanceLevels',
        'No performance levels found in tenant settings',
      );
      return [];
    }
    return settings.gradingSettings.performanceLevels;
  },

  getPerformanceLevelIds: () => {
    const levels = get().getPerformanceLevels();
    return levels.map((level) => level.id);
  },

  getDimensionColors: () => {
    // Union of essay dims and oral-exam dims, deduped, so both surfaces map
    // to the generated $dim<N>700 palette via a single coherent index.
    const allKeys = Array.from(new Set([
      ...get().getEvaluationDimensionKeys(),
      ...get().getOralExamDimensionKeys(),
    ]));
    const colors: Record<string, string> = {};

    allKeys.forEach((dim, index) => {
      colors[dim] = `$dim${index % 10}700`;
    });

    return colors;
  },

  getDimensionLightColors: () => {
    const allKeys = Array.from(new Set([
      ...get().getEvaluationDimensionKeys(),
      ...get().getOralExamDimensionKeys(),
    ]));
    const colors: Record<string, string> = {};

    allKeys.forEach((dim, index) => {
      colors[dim] = `$dim${index % 10}50`;
    });

    return colors;
  },

  getLevelWeights: () => {
    const levels = get().getPerformanceLevels();
    const weights: Record<string, number> = {};

    // Map level IDs to their performance percentages
    levels.forEach((level) => {
      weights[level.id] = level.performancePercent;
    });

    return weights;
  },

  getRawDimensionMaxWeights: () => {
    const { settings } = get();
    if (!settings) {
      return {};
    }
    if (!settings.gradingSettings?.evalDimensions) {
      warnMissingGradingFieldOnce(
        settings,
        'getRawDimensionMaxWeights',
        'No evaluation dimensions found in tenant settings',
      );
      return {};
    }

    const dimensions = settings.gradingSettings.evalDimensions;
    const weights: Record<string, number | null> = {};

    // Extract the maxWeight property from each dimension
    Object.entries(dimensions).forEach(([key, dimension]) => {
      weights[key] = dimension.maxWeight || null;
    });

    return weights;
  },

  // Event kinds methods
  getEventKinds: () => {
    const { settings } = get();
    return Object.values(settings?.eventKinds || {}) as EventKind[];
  },
  getEventKindById: (id) => {
    const kinds = get().getEventKinds();
    return kinds.find((k) => k.id === id) || null;
  },

  // Theme methods
  getTheme: () => {
    const { settings } = get();
    return settings?.theme || null;
  },
  getLegacyCourseCompatibilityMode: () => {
    const { settings } = get();
    return settings?.processSettings?.legacyCourseCompatibilityMode || false;
  },
}));

/**
 * Type-safe hook for accessing grading settings.
 * Use this when you need evaluation dimensions and performance levels.
 * @returns Grading settings and helper functions
 */
export const useTenantGradingSettings = () => {
  const settings = useTenantSettingsStore((state) => state.settings);
  const getEvaluationDimensions = useTenantSettingsStore((state) => state.getEvaluationDimensions);
  const getEvaluationDimensionKeys = useTenantSettingsStore((state) => state.getEvaluationDimensionKeys);
  const getEditableDimensionKeys = useTenantSettingsStore((state) => state.getEditableDimensionKeys);
  const getOralExamDimensionKeys = useTenantSettingsStore((state) => state.getOralExamDimensionKeys);
  const getPerformanceLevels = useTenantSettingsStore((state) => state.getPerformanceLevels);
  const getPerformanceLevelIds = useTenantSettingsStore((state) => state.getPerformanceLevelIds);
  const getDimensionColors = useTenantSettingsStore((state) => state.getDimensionColors);
  const getDimensionLightColors = useTenantSettingsStore((state) => state.getDimensionLightColors);
  const getLevelWeights = useTenantSettingsStore((state) => state.getLevelWeights);
  const getRawDimensionMaxWeights = useTenantSettingsStore((state) => state.getRawDimensionMaxWeights);

  return React.useMemo(() => {
    const results = {
      evalDimensions: getEvaluationDimensions(),
      evalDimensionKeys: getEvaluationDimensionKeys(),
      editableDimensionKeys: getEditableDimensionKeys(),
      oralExamDimensionKeys: getOralExamDimensionKeys(),
      performanceLevels: getPerformanceLevels(),
      performanceLevelIds: getPerformanceLevelIds(),
      dimensionColors: getDimensionColors(),
      dimensionLightColors: getDimensionLightColors(),
      levelWeights: getLevelWeights(),
      dimensionMaxWeights: getRawDimensionMaxWeights(),
    };
    // Include settings in the memo body to satisfy the linter
    return settings ? results : results;
  }, [
    settings,
    getEvaluationDimensions,
    getEvaluationDimensionKeys,
    getEditableDimensionKeys,
    getOralExamDimensionKeys,
    getPerformanceLevels,
    getPerformanceLevelIds,
    getDimensionColors,
    getDimensionLightColors,
    getLevelWeights,
    getRawDimensionMaxWeights,
  ]);
};

/**
 * Type-safe hook for accessing event kinds.
 * @returns Event kinds array and helpers
 */
export const useTenantEventKinds = () => {
  const settings = useTenantSettingsStore((state) => state.settings);
  const getEventKinds = useTenantSettingsStore((state) => state.getEventKinds);
  const getEventKindById = useTenantSettingsStore((state) => state.getEventKindById);

  const eventKinds = React.useMemo(() => {
    const kinds = getEventKinds();
    return settings ? kinds : kinds;
  }, [settings, getEventKinds]);

  const eventKindMap = React.useMemo(() => eventKinds.reduce((acc, kind) => {
    const newMap = { ...acc };
    newMap[kind.id] = kind.label;
    return newMap;
  }, {} as Record<string, string>), [eventKinds]);

  return {
    eventKinds,
    eventKindMap,
    getEventKindById,
  };
};

/**
 * Wrapper that populates the tenant settings store and wraps the whole app
 */
export const TenantSettingsLoader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { setSettings, setStatus } = useTenantSettingsStore(useShallow((state) => ({
    setSettings: state.setSettings,
    setStatus: state.setStatus,
  })));

  const isInitializing = useAuthStore((state) => state.isInitializing);
  const hasBaseURL = !!axios.defaults.baseURL;

  const query = useGetPublicTenantSettings({
    query: {
      enabled: !isInitializing && hasBaseURL, // Only start loading tenant settings after auth is initialized AND baseURL is set
      retry: (failureCount, error) => {
        // Only retry up to 3 times
        if (failureCount >= 3) return false;

        // Never retry 404s - the tenant simply doesn't exist on this hostname
        if (error instanceof AxiosError && error.response?.status === 404) {
          return false;
        }

        // Only retry on timeout or network errors
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          // ECONNABORTED is timeout
          // No status means network error (DNS failure, offline, etc.)
          return error.code === 'ECONNABORTED' || !status || (status >= 500 && status <= 599);
        }
        return false;
      },
      retryDelay: 1000,
      refetchInterval: 30 * 60 * 1000, // Poll every 30 minutes
      refetchOnWindowFocus: false, // Disable refetch on window focus
      staleTime: 30 * 60 * 1000, // Consider data fresh for 30 minutes
      select: (response) => response.data.data,
    },
  });

  // Handle query status changes
  React.useEffect(() => {
    if (query.data) {
      setSettings(query.data);
      setStatus('success');
    }
  }, [query.data, setSettings, setStatus]);

  React.useEffect(() => {
    if (query.error) {
      // On error, we proceed to set status to error, but only after setSettings(null)
      // has initialized the gluestackConfig with the default theme. This ensures
      // ThemeWrapper can mount GluestackUIProvider while SplashScreen shows the error.

      // Enhanced error logging to diagnose 404 issues
      const errorDetails = {
        message: query.error.message,
        status: query.error.response?.status,
        statusText: query.error.response?.statusText,
        // Axios request config details
        requestURL: query.error.config?.url,
        requestBaseURL: query.error.config?.baseURL,
        requestMethod: query.error.config?.method,
        // Final resolved URL (if available)
        responseURL: query.error.request?.responseURL,
        // Headers that were sent
        requestHeaders: {
          Authorization: query.error.config?.headers?.Authorization ? '[REDACTED]' : undefined,
          Origin: query.error.config?.headers?.Origin,
          'X-Tenant-Id': query.error.config?.headers?.['X-Tenant-Id'],
        },
        // Response error details
        responseData: query.error.response?.data,
        // Environment context
        env: {
          EXPO_PUBLIC_API_URL: process.env.EXPO_PUBLIC_API_URL,
          currentURL: typeof window !== 'undefined' ? window.location.href : 'N/A',
        },
      };

      log.warn('Failed to load tenant settings, using defaults', errorDetails);
      setSettings(null);
      setStatus('error');
    }
  }, [query.error, setSettings, setStatus]);

  React.useEffect(() => {
    if (query.isLoading) {
      setStatus('loading');
    }
  }, [query.isLoading, setStatus]);

  // APPROACH 1: Do not render children until settings are loaded and infused
  // This ensures GluestackUIProvider mounts once and only once with the final config
  // if (query.isLoading && !isInitializing) {
  //   return null; // Keep splash screen visible
  // }

  return children;
};

export default useTenantSettingsStore;
